import { useCallback, useEffect, useState } from "react";
import * as apiClient from "@/services/api-client";
import type { AuditListItemExtended } from "@/types";
import {
  computeDashboardMetrics,
  type DashboardMetrics,
} from "@/features/dashboard/dashboard-metrics";

const OVERVIEW_LIMIT = 100;

type TranslationFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export interface DashboardOverview extends DashboardMetrics {
  audits: AuditListItemExtended[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Loads a recent audit-list page and derives dashboard KPIs client-side.
 * Reuses GET /audits — no aggregate metrics endpoint (pirx-3apc9 thin path).
 */
export function useDashboardOverview(
  { t }: { t?: TranslationFunction } = {},
): DashboardOverview {
  const [audits, setAudits] = useState<AuditListItemExtended[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) setLoading(true);

      const data = await apiClient.listAuditHistory({
        page: 1,
        limit: OVERVIEW_LIMIT,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      setAudits(data.audits);
      setTotal(data.total);
      setError(null);
    } catch (err) {
      if (err instanceof apiClient.ApiError) {
        setError(err.message);
      } else {
        setError(t?.("errors.loadingFailed") ?? "Failed to load dashboard");
      }
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  const metrics = computeDashboardMetrics(audits);

  return {
    ...metrics,
    audits,
    total,
    loading,
    error,
    refresh: () => fetchOverview(false),
  };
}
